import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { SocketProvider } from './contexts/SocketContext';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ViewingAsProvider } from './contexts/ViewingAsContext';

// Components
import Layout from './components/Layout';
import Gate from './pages/Gate';
import MascotPreview from './pages/MascotPreview';
import Dashboard from './pages/Dashboard';
import Models from './pages/Models';
import Accounts from './pages/Accounts';
import Proxies from './pages/Proxies';
import BartProxies from './pages/BartProxies';
import Scraper from './pages/Scraper';
import ScraperV2 from './pages/ScraperV2';
import MassDMs from './pages/MassDMs';
import MassLikes from './pages/MassLikes';
import MassFollows from './pages/MassFollows';
import MassTasks from './pages/MassTasks';
import AISettings from './pages/AISettings';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Register from './pages/Register';
import AdminUsers from './pages/AdminUsers';
import AdminVAs from './pages/AdminVAs';
import FollowbackProfileComparison from './pages/admin/FollowbackProfileComparison';
import OrbexLicenses from './pages/admin/OrbexLicenses';
import OrbexAccounts from './pages/admin/OrbexAccounts';
import VpsOverview from './pages/admin/vps/VpsOverview';
import VpsAutomations from './pages/admin/vps/VpsAutomations';
import VpsUserProfiles from './pages/admin/vps/VpsUserProfiles';
import VpsPerformance from './pages/admin/vps/VpsPerformance';
import VpsFollowback from './pages/admin/vps/VpsFollowback';
import VpsAccounts from './pages/admin/vps/VpsAccounts';
import VpsNotifHealth from './pages/admin/vps/VpsNotifHealth';
import VpsWarmup from './pages/admin/vps/VpsWarmup';
import Billing from './pages/Billing';
import AutoComments from './pages/AutoComments';
import UpdateAccounts from './pages/UpdateAccounts';
import ApiManagement from './pages/ApiManagement';
import Chatbot from './pages/Chatbot';
import ChatLinks from './pages/ChatLinks';
import PublicChat from './pages/PublicChat';
import SnapChatbot from './pages/SnapChatbot';
import VALayout from './layouts/VALayout';
import VAOverview from './pages/va/VAOverview';
import VATargetUsernames from './pages/va/VATargetUsernames';
import VAProfileAccounts from './pages/va/VAProfileAccounts';
import VAGuides from './pages/va/VAGuides';
import VALeaderboard from './pages/va/VALeaderboard';

// Styles
import './index.css';
import { getSocketOrigin } from './services/api';

function initialChatGate() {
  if (typeof window === 'undefined') return 'app';
  const path = window.location.pathname;
  const host = window.location.hostname;
  const appHosts = new Set(['localhost', '127.0.0.1', 'wingr.io', 'www.wingr.io']);
  const appPath = path === '/'
    || path.startsWith('/dashboard')
    || path.startsWith('/login')
    || path.startsWith('/register')
    || path.startsWith('/va')
    || path.startsWith('/mascot-preview');
  if (path.startsWith('/c/')) return 'chat';
  if (appHosts.has(host) && appPath) return 'app';
  return 'checking';
}

function ChatHostGate({ children }) {
  const location = useLocation();
  const [gate, setGate] = React.useState(initialChatGate);

  React.useEffect(() => {
    const path = location.pathname;
    const host = window.location.hostname;
    const appHosts = new Set(['localhost', '127.0.0.1', 'wingr.io', 'www.wingr.io']);
    const appPath = path === '/'
      || path.startsWith('/dashboard')
      || path.startsWith('/login')
      || path.startsWith('/register')
      || path.startsWith('/va')
      || path.startsWith('/mascot-preview');
    if (path.startsWith('/c/')) {
      setGate('chat');
      return undefined;
    }
    if (appHosts.has(host) && appPath) {
      setGate('app');
      return undefined;
    }
    const url = `${getSocketOrigin()}/api/public/chat/resolve?host=${encodeURIComponent(host)}&path=${encodeURIComponent(path)}`;
    let cancelled = false;
    fetch(url)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!cancelled) setGate(data?.profile ? 'chat' : 'app');
      })
      .catch(() => {
        if (!cancelled) setGate('app');
      });
    return () => { cancelled = true; };
  }, [location.pathname]);

  if (gate === 'checking') {
    return <div className="min-h-screen bg-black" />;
  }
  if (gate === 'chat') {
    return <PublicChat />;
  }
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ViewingAsProvider>
        <SocketProvider>
          <Router>
          <ChatHostGate>
          <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
            <Routes>
              {/* Public landing page */}
              <Route path="/" element={<Gate />} />
              <Route path="/mascot-preview" element={<MascotPreview />} />
              <Route path="/login" element={<Navigate to="/" replace />} />
              <Route path="/register" element={<Register />} />
              
              {/* Protected dashboard routes */}
              <Route path="/dashboard" element={<Layout />}>
                <Route index element={<Dashboard />} />
                <Route path="models" element={<Models />} />
                <Route path="accounts" element={<Accounts />} />
                <Route path="chatbot" element={<Chatbot />} />
                <Route path="chat-links" element={<ChatLinks />} />
                <Route path="snapchat" element={<SnapChatbot />} />
                <Route path="proxies" element={<Proxies />} />
                <Route path="bart-proxies" element={<BartProxies />} />
                <Route path="scraper" element={<Scraper />} />
                <Route path="scraper-v2" element={<ScraperV2 />} />
                <Route path="mass-dms" element={<MassDMs />} />
                <Route path="mass-likes" element={<MassLikes />} />
                <Route path="mass-follows" element={<MassFollows />} />
                <Route path="mass-tasks" element={<MassTasks />} />
                <Route path="auto-comments" element={<AutoComments />} />
                <Route path="update-accounts" element={<UpdateAccounts />} />
                <Route path="ai-settings" element={<AISettings />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="api" element={<ApiManagement />} />
                <Route path="settings" element={<Settings />} />
                <Route path="billing" element={<Billing />} />
                <Route path="admin/users" element={<AdminUsers />} />
                <Route path="admin/vas" element={<AdminVAs />} />
                <Route path="admin/followback-profile-comparison" element={<FollowbackProfileComparison />} />
                <Route path="admin/orbex-licenses" element={<OrbexLicenses />} />
                <Route path="admin/orbex-accounts" element={<OrbexAccounts />} />
                <Route path="admin/vps" element={<VpsOverview />} />
                <Route path="admin/vps/automations" element={<VpsAutomations />} />
                <Route path="admin/vps/user-profiles" element={<VpsUserProfiles />} />
                <Route path="admin/vps/performance" element={<VpsPerformance />} />
                <Route path="admin/vps/followback" element={<VpsFollowback />} />
                <Route path="admin/vps/accounts" element={<VpsAccounts />} />
                <Route path="admin/vps/notif-health" element={<VpsNotifHealth />} />
                <Route path="admin/vps/warmup" element={<VpsWarmup />} />
              </Route>

              {/* Virtual Assistant portal — isolated from main dashboard */}
              <Route path="/va" element={<VALayout />}>
                <Route index element={<VAOverview />} />
                <Route path="leaderboard" element={<VALeaderboard />} />
                <Route path="guides" element={<VAGuides />} />
                <Route path="targets" element={<VATargetUsernames />} />
                <Route path="accounts" element={<VAProfileAccounts />} />
              </Route>
              
              {/* Redirect old routes to dashboard */}
              <Route path="/models" element={<Navigate to="/dashboard/models" replace />} />
              <Route path="/accounts" element={<Navigate to="/dashboard/accounts" replace />} />
              <Route path="/proxies" element={<Navigate to="/dashboard/proxies" replace />} />
              <Route path="/ai-settings" element={<Navigate to="/dashboard/ai-settings" replace />} />
              <Route path="/analytics" element={<Navigate to="/dashboard/analytics" replace />} />
              <Route path="/settings" element={<Navigate to="/dashboard/settings" replace />} />
            </Routes>
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#22c55e',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
          </div>
          </ChatHostGate>
          </Router>
        </SocketProvider>
        </ViewingAsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
